import { useEffect, useRef, useState } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';
import { FaGraduationCap, FaRocket, FaTrophy, FaCode, FaSlidersH, FaTools } from 'react-icons/fa';

// Real-Time YOLOv8/ML Inference Telemetry Console
const TelemetryDashboard = () => {
  const [logs, setLogs] = useState([]);
  const [gpuLoad, setGpuLoad] = useState(74);
  const [vram, setVram] = useState(8.42);
  const [fps, setFps] = useState(84);

  const mockLogs = [
    'SYS // Initializing YOLOv8 inference thread...',
    'SYS // Model compiled successfully (TensorRT engine).',
    'SYS // Pipeline source: rtsp://10.12.4.92/ch01_live',
    'DET // Bounding box matched: person (0.94) at [241, 112, 380, 521]',
    'DET // Bounding box matched: safety-helmet (0.91) at [245, 115, 290, 160]',
    'DET // Bounding box matched: vest (0.89) at [252, 162, 330, 310]',
    'SYS // Inference cycle execution time: 11.4ms',
    'MET // Epoch evaluation mAP50: 0.945 | mAP50-95: 0.782',
    'SYS // Core temperature: 68°C | Fan speed: 45%',
    'SYS // Buffer cache: 98% OK | MLOps telemetry active.',
  ];

  useEffect(() => {
    // Initial logs fill
    setLogs([mockLogs[0], mockLogs[1], mockLogs[2]]);
    let index = 3;
    
    const interval = setInterval(() => {
      setLogs((prev) => {
        const nextLogs = [...prev, mockLogs[index]];
        if (nextLogs.length > 4) {
          nextLogs.shift();
        }
        return nextLogs;
      });
      
      // Dynamic adjustments for realism
      setGpuLoad(Math.floor(70 + Math.random() * 8));
      setVram(parseFloat((8.2 + Math.random() * 0.45).toFixed(2)));
      setFps(Math.floor(80 + Math.random() * 7));

      index = (index + 1) % mockLogs.length;
    }, 1800);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="border border-grey-800 bg-elevated/30 p-5 font-mono text-[10px] space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center border-b border-grey-800 pb-2">
        <span className="text-white-pure font-bold flex items-center gap-1.5 uppercase tracking-widest">
          <span className="w-1.5 h-1.5 bg-white-pure animate-pulse" />
          TELEMETRY // INFERENCE_LOGS [YOLOv8s]
        </span>
        <span className="text-grey-600 text-[8px]">REF: COCO_VAL2017</span>
      </div>

      {/* Grid Stats */}
      <div className="grid grid-cols-3 gap-2 text-center border-b border-grey-800 pb-3">
        <div className="bg-void p-2 border border-grey-800">
          <div className="text-grey-600 uppercase text-[8px] tracking-wider">GPU_LOAD</div>
          <div className="text-white-pure font-bold text-xs mt-0.5 number-readout">{gpuLoad}%</div>
        </div>
        <div className="bg-void p-2 border border-grey-800">
          <div className="text-grey-600 uppercase text-[8px] tracking-wider">VRAM_USE</div>
          <div className="text-white-pure font-bold text-xs mt-0.5 number-readout">{vram} GB</div>
        </div>
        <div className="bg-void p-2 border border-grey-800">
          <div className="text-grey-600 uppercase text-[8px] tracking-wider">LATENCY</div>
          <div className="text-white-pure font-bold text-xs mt-0.5 number-readout">{fps} FPS</div>
        </div>
      </div>

      {/* Streaming Console */}
      <div className="space-y-1 bg-void p-3 border border-grey-800 min-h-[92px] flex flex-col justify-end text-grey-400">
        {logs.map((log, i) => (
          <div key={i} className="truncate tracking-wide text-left">
            <span className="text-grey-600 font-bold mr-1.5">&gt;</span>
            {log}
          </div>
        ))}
      </div>
    </div>
  );
};

const HighlightCard = ({ icon: Icon, title, description, detail, variants }) => {
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    
    const mouseX = e.clientX - rect.left - width / 2;
    const mouseY = e.clientY - rect.top - height / 2;
    
    const rX = -(mouseY / (height / 2)) * 4;
    const rY = (mouseX / (width / 2)) * 4;
    
    cardRef.current.style.transform = `perspective(1000px) rotateX(${rX}deg) rotateY(${rY}deg) scale3d(1.01, 1.01, 1.01)`;
  };

  const handleMouseLeave = () => {
    if (!cardRef.current) return;
    cardRef.current.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)`;
  };

  const renderBrackets = () => (
    <>
      <span className="reticle-bracket top-0 left-0 border-t border-l border-white-pure group-hover:translate-x-0.5 group-hover:translate-y-0.5 group-hover:opacity-100" />
      <span className="reticle-bracket top-0 right-0 border-t border-r border-white-pure group-hover:-translate-x-0.5 group-hover:translate-y-0.5 group-hover:opacity-100" />
      <span className="reticle-bracket bottom-0 left-0 border-b border-l border-white-pure group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:opacity-100" />
      <span className="reticle-bracket bottom-0 right-0 border-b border-r border-white-pure group-hover:-translate-x-0.5 group-hover:-translate-y-0.5 group-hover:opacity-100" />
    </>
  );

  return (
    <motion.div
      ref={cardRef}
      variants={variants}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="about-card hud-card hud-card-hover group cursor-pointer transition-all duration-150 p-6 flex flex-col justify-between"
      style={{ transformStyle: 'preserve-3d' }}
    >
      {renderBrackets()}
      
      <div>
        <div className="w-10 h-10 bg-grey-800 flex items-center justify-center mb-5 border border-grey-600">
          <Icon className="text-sm text-white-primary" />
        </div>
        <h3 className="text-sm font-mono tracking-wider font-bold mb-1.5 uppercase text-white-pure">{title}</h3>
        <p className="text-xs text-grey-400 font-sans leading-relaxed">{description}</p>
      </div>
      <p className="text-[10px] font-mono tracking-widest text-grey-600 uppercase mt-4">{detail}</p>
    </motion.div>
  );
};

const About = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.1 });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start('visible');
    }
  }, [isInView, controls]);

  const highlights = [
    {
      icon: FaGraduationCap,
      title: '01 // Education',
      description: 'B.Tech candidate in Artificial Intelligence & Data Science with structural honors.',
      detail: 'Karunya University',
    },
    {
      icon: FaCode,
      title: '02 // Expertise',
      description: 'Real-time deep learning model customization, inferencing, & REST API orchestrations.',
      detail: 'YOLOv8 / React / FastAPI',
    },
    {
      icon: FaTrophy,
      title: '03 // Hackathons',
      description: 'Multiple national awards secured for innovative object detection and municipal logistics.',
      detail: '6+ Tech Certifications',
    },
    {
      icon: FaRocket,
      title: '04 // MLOps Focus',
      description: 'Optimizing inference speed on CPU/GPU endpoints, and managing cloud container nodes.',
      detail: 'Scaling Real-world AI',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1],
      },
    },
  };

  return (
    <section
      id="about"
      ref={sectionRef}
      className="section-padding relative overflow-hidden bg-void border-t border-grey-800"
    >
      <div className="container-custom relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={controls}
          className="mb-20 text-left border-l-2 border-white-primary pl-6"
        >
          <motion.span
            variants={itemVariants}
            className="inline-block text-[9px] font-mono font-bold tracking-[0.35em] uppercase text-grey-400 mb-2"
          >
            01 // PROFILE_DATA
          </motion.span>
          <motion.h2
            variants={itemVariants}
            className="text-3xl sm:text-5xl md:text-6xl font-display font-black"
          >
            ABOUT // <span className="chrome-text">ME</span>
          </motion.h2>
        </motion.div>

        {/* Grid content */}
        <div className="grid lg:grid-cols-12 gap-12 items-start mb-16">
          
          {/* Left specs column: Text description + interactive telemetry panel */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={controls}
            className="lg:col-span-7 space-y-8"
          >
            <div className="space-y-5">
              <motion.p
                variants={itemVariants}
                className="text-sm sm:text-base text-grey-200 leading-relaxed font-sans"
              >
                I am an AI & Data Science engineer focused on the engineering of{' '}
                <span className="text-white-pure font-bold">deep learning models, computer vision systems</span>, and{' '}
                <span className="text-white-pure font-bold">full-stack MLOps infrastructure</span>. My work centres around optimization — compiling models for edge performance and developing async interface layers in Python, React, and Docker.
              </motion.p>

              <motion.p
                variants={itemVariants}
                className="text-sm sm:text-base text-grey-200 leading-relaxed font-sans"
              >
                Leveraging data intelligence, I construct solutions for industrial compliance and logistics. Through multiple national-level hackathons, I have designed and deployed real-time queue-density estimation and worker safety systems.
              </motion.p>
            </div>

            <motion.div variants={itemVariants} className="flex flex-wrap gap-2 pt-2">
              {['AI/ML Engineering', 'Computer Vision', 'Full Stack Dev', 'MLOps & Scaling'].map((skill) => (
                <span
                  key={skill}
                  className="px-3 py-1.5 border border-grey-800 bg-elevated text-[10px] font-mono tracking-wider text-grey-400"
                >
                  {skill}
                </span>
              ))}
            </motion.div>

            {/* Injected Model Telemetry Dashboard fills wasted space perfectly */}
            <motion.div variants={itemVariants} className="pt-2">
              <TelemetryDashboard />
            </motion.div>
          </motion.div>

          {/* Right specs column: Portrait Vignette Card */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={controls}
            className="lg:col-span-5 flex justify-center lg:justify-end"
          >
            <motion.div 
              variants={itemVariants}
              className="relative group p-2 bg-elevated border border-grey-800 shadow-lg max-w-[360px] w-full"
            >
              <div className="aspect-square overflow-hidden relative bg-void border border-grey-600">
                <img 
                  src="/images/profile.png" 
                  alt="Jerwin Titus D"
                  className="w-full h-full object-cover grayscale brightness-90 contrast-110 group-hover:scale-105 transition-transform duration-500 ease-out"
                />
                <div className="absolute inset-0 bg-fine-noise opacity-15 pointer-events-none" />
                <div className="absolute inset-0 shadow-[inset_0_0_40px_rgba(0,0,0,0.9)]" />
              </div>

              {/* Stats panel */}
              <div className="grid grid-cols-3 gap-1 mt-4 border-t border-grey-800 pt-4 text-center font-mono">
                <div>
                  <div className="text-xl font-bold text-white-pure number-readout">10+</div>
                  <div className="text-[8px] tracking-wider text-grey-400 uppercase mt-0.5">DEPLOYED</div>
                </div>
                <div>
                  <div className="text-xl font-bold text-white-pure number-readout">06+</div>
                  <div className="text-[8px] tracking-wider text-grey-400 uppercase mt-0.5">AWARDS</div>
                </div>
                <div>
                  <div className="text-xl font-bold text-white-pure number-readout">01+</div>
                  <div className="text-[8px] tracking-wider text-grey-400 uppercase mt-0.5">YEARS_EXP</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* 4 highlights: Rendered via Framer Motion to guarantee they show in viewport */}
        <motion.div 
          className="about-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-4"
          variants={containerVariants}
          initial="hidden"
          animate={controls}
        >
          {highlights.map((item, index) => (
            <HighlightCard
              key={index}
              icon={item.icon}
              title={item.title}
              description={item.description}
              detail={item.detail}
              variants={itemVariants}
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default About;
